import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Button,
  Alert,
  Tooltip,
  Badge,
  useTheme,
  alpha,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ViewCompact as CompactIcon,
  Assignment as AssignmentIcon,
  GroupWork as GroupIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import type {
  MedicalReservationRecord,
  MedicalReservationUpdateRequest,
  ActionStatus,
  BulkActionType,
} from '../types/medicalReservation';
import { ACTION_STATUS_CONFIG } from '../types/medicalReservation';
import { ReservationTable } from '../components/MedicalReservation/ReservationTable';
import { DetailModal } from '../components/MedicalReservation/DetailModal';
import { EditModal } from '../components/MedicalReservation/EditModal';
import { AssignModal } from '../components/MedicalReservation/AssignModal';
import { FilterDrawer } from '../components/MedicalReservation/FilterDrawer';
import { PasteConfirmDialog } from '../components/MedicalReservation/PasteConfirmDialog';
import { useMedicalReservationList } from '../hooks/useMedicalReservationList';
import { useNotification } from '../hooks/useNotification';

/**
 * 診療予約データ一覧画面（レコード管理版）
 * 
 * 意図：
 * - 個別診療予約レコードの一覧表示と管理
 * - 担当医師・患者・ステータス等による高度な検索・フィルタリング
 * - 詳細表示・編集・一括操作による効率的な運用サポート
 * 
 * 注意点：
 * - 大量レコード表示時のパフォーマンス最適化
 * - 楽観的更新によるレスポンシブなUI
 * - アクセシビリティとユーザビリティの確保
 * 
 * 改善提案：
 * - リアルタイム更新（WebSocket）
 * - CSV/PDFエクスポート機能
 * - カスタムダッシュボード
 */
export const MedicalReservationListPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const {
    data,
    meta,
    isLoading,
    error,
    filters,
    selectedRecord,
    detailModalOpen,
    assignModalOpen,
    selectedIds,
    updateFilters,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleSearch,
    refresh,
    showDetails,
    showAssignModal,
    closeModals,
    deleteRecord,
    assignRecord,
    toggleNotificationStatus,
    clearError,
    toggleSelectRecord,
    selectAllRecords,
    clearSelection,
    pasteDialogOpen,
    pasteStatus,
    isProcessing,
    openPasteDialog,
    closePasteDialog,
    pasteSelectedRecords,
  } = useMedicalReservationList();

  // UI状態管理
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<BulkActionType>('paste');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<MedicalReservationRecord | null>(null);
  const [compactMode, setCompactMode] = useState(() => {
    const saved = localStorage.getItem('mederi_compact_mode');
    return saved === 'true';
  });

  // 未実装の状態管理（仮実装）
  const editModalOpen = false;
  const updateRecord = async (id: string, data: any) => ({} as MedicalReservationRecord);
  const bulkUpdateActionStatus = async (ids: string[], status: ActionStatus, assignedTo?: string) => true;
  const closeDetailModal = closeModals;
  const closeEditModal = closeModals;
  const closeAssignModal = closeModals;

  // レコード詳細表示
  const handleViewDetails = useCallback((record: MedicalReservationRecord) => {
    showDetails(record);
  }, [showDetails]);

  // レコード貼り付け（個別レコードから）
  const handlePaste = useCallback((record: MedicalReservationRecord) => {
    // 選択状態をクリアして、このレコードのみを選択
    clearSelection();
    toggleSelectRecord(record.id);
    // 貼り付けダイアログを開く
    openPasteDialog();
  }, [clearSelection, toggleSelectRecord, openPasteDialog]);

  // レコード振り分け
  const handleAssign = useCallback((record: MedicalReservationRecord) => {
    showAssignModal(record);
  }, [showAssignModal]);

  // レコード削除確認
  const handleDeleteClick = useCallback((record: MedicalReservationRecord) => {
    setRecordToDelete(record);
    setDeleteConfirmOpen(true);
  }, []);

  // レコード削除実行
  const handleDeleteConfirm = useCallback(async () => {
    if (!recordToDelete) return;
    
    try {
      const success = await deleteRecord(recordToDelete.id);
      if (success) {
        showNotification('success', 'レコードを削除しました');
        setDeleteConfirmOpen(false);
        setRecordToDelete(null);
      }
    } catch (error) {
      showNotification('error', 'レコードの削除に失敗しました');
    }
  }, [recordToDelete, deleteRecord, showNotification]);

  // 削除キャンセル
  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
    setRecordToDelete(null);
  }, []);

  // レコード更新
  const handleSaveRecord = useCallback(async (id: string, updateData: MedicalReservationUpdateRequest) => {
    try {
      const updatedRecord = await updateRecord(id, updateData);
      showNotification('success', 'レコードを更新しました');
      return updatedRecord;
    } catch (error) {
      showNotification('error', 'レコードの更新に失敗しました');
      throw error;
    }
  }, [updateRecord, showNotification]);

  // レコード振り分け実行
  const handleAssignRecord = useCallback(async (id: string, assignedTo: string, notes?: string) => {
    try {
      const success = await assignRecord(id, assignedTo, notes);
      if (success) {
        showNotification('success', `${assignedTo}に振り分けました`);
        // 型互換性のための仮実装
        return {} as MedicalReservationRecord;
      }
      throw new Error('振り分けに失敗しました');
    } catch (error) {
      showNotification('error', '振り分けに失敗しました');
      throw error;
    }
  }, [assignRecord, showNotification]);

  // 一括操作実行
  const handleBulkAction = useCallback(async () => {
    if (selectedIds.length === 0) {
      showNotification('warning', 'レコードを選択してください');
      return;
    }

    switch (bulkActionType) {
      case 'paste':
        // 一括操作ダイアログを閉じて貼り付けダイアログを開く
        setBulkActionDialogOpen(false);
        openPasteDialog();
        break;
      case 'delete':
        // 将来実装
        showNotification('info', 'この機能は開発中です');
        break;
      case 'notify':
        // 将来実装
        showNotification('info', 'この機能は開発中です');
        break;
    }
  }, [selectedIds, bulkActionType, openPasteDialog, showNotification]);

  // コンパクトモードの切り替え
  const handleCompactModeToggle = useCallback(() => {
    const newMode = !compactMode;
    setCompactMode(newMode);
    localStorage.setItem('mederi_compact_mode', String(newMode));
  }, [compactMode]);

  // モック担当医師オプション
  const doctorOptions = ['田中医師', '佐藤医師', '山田医師'];

  // アクティブフィルター数の計算
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.doctor) count++;
    if (filters.actionStatus?.length) count++;
    if (filters.dateRange?.start || filters.dateRange?.end) count++;
    return count;
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.grey[50],
        py: compactMode ? 2 : 3,
        maxWidth: 1200,
        mx: 'auto',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* ヘッダーセクション */}
        <Paper
          elevation={0}
          sx={{
            px: compactMode ? 3 : 4,
            py: compactMode ? 2 : 3,
            mb: compactMode ? 2 : 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography
                variant={compactMode ? "h5" : "h4"}
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: compactMode ? 0 : 0.5,
                }}
              >
                診療予約データ一覧
              </Typography>
              {!compactMode && (
                <Typography variant="body2" color="text.secondary">
                  診療予約レコードの一覧表示・検索・管理
                </Typography>
              )}
              {meta && (
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Chip 
                    label={`総レコード数: ${meta.total_records}`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                  {selectedIds.length > 0 && (
                    <Chip 
                      label={`選択中: ${selectedIds.length}件`} 
                      size="small" 
                      color="secondary"
                    />
                  )}
                </Stack>
              )}
            </Box>
            <Stack direction="row" spacing={compactMode ? 1 : 2}>
              <Tooltip title="表示モード切替" placement="bottom">
                <IconButton
                  onClick={handleCompactModeToggle}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <CompactIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="データを更新" placement="bottom">
                <IconButton
                  onClick={refresh}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => navigate('/appointments/upload')}
                sx={{
                  px: compactMode ? 2 : 3,
                  py: compactMode ? 1 : 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: theme.shadows[4],
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[8],
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                CSVアップロード
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* 検索・フィルターバー */}
        <Box sx={{ px: compactMode ? 3 : 4, mb: compactMode ? 2 : 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: compactMode ? 1.5 : 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <TextField
                placeholder="anybot ID、患者名、担当医師で検索..."
                value={filters.search || ''}
                onChange={(e) => updateFilters({ search: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                  },
                }}
                sx={{ minWidth: 300, flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                endIcon={<ArrowDownIcon />}
                onClick={() => setFilterDrawerOpen(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: theme.palette.divider,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                フィルター
                {getActiveFilterCount() > 0 && (
                  <Badge
                    badgeContent={getActiveFilterCount()}
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                    }}
                  />
                )}
              </Button>
              {selectedIds.length > 0 && (
                <Button
                  variant="contained"
                  startIcon={<GroupIcon />}
                  onClick={() => setBulkActionDialogOpen(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                >
                  一括操作
                </Button>
              )}
            </Stack>

            {/* アクティブフィルターの表示 */}
            <AnimatePresence>
              {getActiveFilterCount() > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                    {filters.search && (
                      <Chip
                        label={`検索: ${filters.search}`}
                        onDelete={() => updateFilters({ search: '' })}
                        size="small"
                        icon={<SearchIcon />}
                      />
                    )}
                    {filters.doctor && (
                      <Chip
                        label={`担当医師: ${filters.doctor}`}
                        onDelete={() => updateFilters({ doctor: '' })}
                        size="small"
                      />
                    )}
                    {filters.actionStatus?.map((status) => (
                      <Chip
                        key={status}
                        label={ACTION_STATUS_CONFIG[status].label}
                        onDelete={() => updateFilters({ 
                          actionStatus: filters.actionStatus?.filter(s => s !== status) 
                        })}
                        size="small"
                        color={ACTION_STATUS_CONFIG[status].color}
                        icon={<span>{ACTION_STATUS_CONFIG[status].icon}</span>}
                      />
                    ))}
                    {(filters.dateRange?.start || filters.dateRange?.end) && (
                      <Chip
                        label={`期間: ${filters.dateRange?.start || ''} - ${filters.dateRange?.end || ''}`}
                        onDelete={() => updateFilters({ dateRange: undefined })}
                        size="small"
                      />
                    )}
                  </Stack>
                </motion.div>
              )}
            </AnimatePresence>
          </Paper>
        </Box>

        {/* データテーブル */}
        <Box sx={{ px: compactMode ? 3 : 4 }}>
          {error ? (
            <Alert 
              severity="error" 
              sx={{ borderRadius: 2, whiteSpace: 'pre-wrap' }}
              action={
                <Button color="inherit" size="small" onClick={clearError}>
                  閉じる
                </Button>
              }
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                データの取得に失敗しました
              </Typography>
              <Typography variant="body2" component="div">
                {error}
              </Typography>
              {process.env.NODE_ENV === 'development' && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
                    デバッグ情報:
                    - エンドポイント: /api/medical-reservations
                    - ベースURL: {process.env.REACT_APP_API_BASE_URL || '未設定'}
                    - トークン: {localStorage.getItem('mederi_auth_token') ? '設定済み' : '未設定'}
                  </Typography>
                </Box>
              )}
            </Alert>
          ) : (
            <ReservationTable
              data={data}
              isLoading={isLoading}
              onViewDetails={handleViewDetails}
              onEdit={handlePaste}
              onAssign={handleAssign}
              onDelete={handleDeleteClick}
              onToggleNotificationStatus={toggleNotificationStatus}
              filters={filters}
              onFiltersChange={updateFilters}
              meta={meta || undefined}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelectRecord}
              onSelectAll={selectAllRecords}
              onClearSelection={clearSelection}
              compactMode={compactMode}
            />
          )}
        </Box>
      </motion.div>

      {/* フローティングアクションボタン */}
      {selectedIds.length > 0 && (
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
          }}
          onClick={() => setBulkActionDialogOpen(true)}
        >
          <GroupIcon />
        </Fab>
      )}

      {/* モーダル群 */}
      <DetailModal
        open={detailModalOpen}
        record={selectedRecord}
        onClose={closeDetailModal}
      />

      <EditModal
        open={editModalOpen}
        record={selectedRecord}
        onClose={closeEditModal}
        onSave={handleSaveRecord}
      />

      <AssignModal
        open={assignModalOpen}
        record={selectedRecord}
        onClose={closeAssignModal}
        onAssign={handleAssignRecord}
      />

      {/* 一括操作ダイアログ */}
      <Dialog
        open={bulkActionDialogOpen}
        onClose={() => setBulkActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <GroupIcon />
            一括操作
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              選択された{selectedIds.length}件のレコードに対して一括操作を実行します。
            </Typography>
            
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">操作タイプ</FormLabel>
              <RadioGroup
                value={bulkActionType}
                onChange={(e) => setBulkActionType(e.target.value as BulkActionType)}
              >
                <FormControlLabel 
                  value="paste" 
                  control={<Radio />} 
                  label="スプレッドシートに貼り付け" 
                />
                <FormControlLabel 
                  value="delete" 
                  control={<Radio />} 
                  label="削除" 
                  disabled
                />
                <FormControlLabel 
                  value="notify" 
                  control={<Radio />} 
                  label="通知設定" 
                  disabled
                />
              </RadioGroup>
            </FormControl>

            {bulkActionType === 'paste' && (
              <Alert severity="info">
                選択されたレコードをGoogleスプレッドシートに貼り付けます。
                貼り付け成功後、ステータスは自動的に「診療リスト登録済み」に更新されます。
              </Alert>
            )}

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleBulkAction}
            variant="contained"
            disabled={false}
          >
            実行
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            レコードの削除確認
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              以下のレコードを削除しますか？
            </Typography>
            {recordToDelete && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>患者名:</strong> {recordToDelete.patientName}
                </Typography>
                <Typography variant="body2">
                  <strong>anybot ID:</strong> {recordToDelete.anybotId}
                </Typography>
                <Typography variant="body2">
                  <strong>担当医師:</strong> {recordToDelete.doctorInCharge}
                </Typography>
                <Typography variant="body2">
                  <strong>診療開始時間:</strong> {format(new Date(recordToDelete.consultationStartTime), 'yyyy/MM/dd HH:mm', { locale: ja })}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              ※ この操作は取り消すことができません。
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>

      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filters}
        onFiltersChange={updateFilters}
        doctorOptions={doctorOptions}
      />

      {/* 貼り付け確認ダイアログ */}
      <PasteConfirmDialog
        open={pasteDialogOpen}
        selectedRecords={data.filter(record => selectedIds.includes(record.id))}
        onClose={closePasteDialog}
        onConfirm={pasteSelectedRecords}
        pasteStatus={pasteStatus}
        isProcessing={isProcessing}
      />
    </Box>
  );
}; 